import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams, useSearchParams, Navigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Form,
  Input,
  Select,
  Button,
  Card,
  Table,
  Space,
  InputNumber,
  Divider,
  Row,
  Col,
  Typography,
  DatePicker,
  Radio,
  Tag,
  Alert,
  Upload,
  Checkbox,
} from "antd";
import {
  PlusOutlined,
  DeleteOutlined,
  EditOutlined,
  CopyOutlined,
  InboxOutlined,
  UploadOutlined,
  FilePdfOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import { toast } from "react-toastify";
import moment from "moment";
import axios from "axios";
import {
  createOrder,
  getOrderById,
  updateOrder,
} from "../../api/orderApi";
import { loadAllCustomer } from "../../redux/actions/customer/getCustomerAction";
import OrderItemModal from "./OrderItemModal";
import OrderBulkItemsModal from "./OrderBulkItemsModal";
import PageTitle from "../page-header/PageHeader";
import checkTokenExp from "../../utils/checkTokenExp";
import PoIngestDropzone from "../poIngest/PoIngestDropzone";
import PoIngestReviewModal from "../poIngest/PoIngestReviewModal";
import { linkPoIngestion, deletePoIngestion } from "../../api/poIngestApi";

const { TextArea } = Input;
const { Option } = Select;
const { Title } = Typography;

const OrderForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const cloneFromId = searchParams.get("cloneFrom");
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const isEditing = !!id;
  const [cloneJobs, setCloneJobs] = useState(!!cloneFromId);

  const customers = useSelector((state) => state.customers?.list);

  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [itemSearch, setItemSearch] = useState("");
  const [itemModalVisible, setItemModalVisible] = useState(false);
  const [bulkItemModalVisible, setBulkItemModalVisible] = useState(false);
  const [editingItemIndex, setEditingItemIndex] = useState(null);
  const [pricingType, setPricingType] = useState("individual"); // "individual" | "inner_combined" | "outer_combined"
  const [poDocUrl, setPoDocUrl] = useState(null);
  const [poFile, setPoFile] = useState(null);
  const [uploadingPo, setUploadingPo] = useState(false);
  const [ingestReviewOpen, setIngestReviewOpen] = useState(false);
  const [currentIngest, setCurrentIngest] = useState(null);
  const [pendingIngestionId, setPendingIngestionId] = useState(null);
  const [markingFile, setMarkingFile] = useState(null);
  const [markingKey, setMarkingKey] = useState(null);
  const [markingFilename, setMarkingFilename] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [fileType, setFileType] = useState(null); // 'pdf' | 'image'
  const [totals, setTotals] = useState({
    subtotal: 0,
    discount_amount: 0,
    tax_amount: 0,
    total_amount: 0,
  });

  // Load customers via Redux
  useEffect(() => {
    dispatch(loadAllCustomer({ page: 1, limit: 100, status: true }));
  }, [dispatch]);

  // Fetch order if editing
  useEffect(() => {
    if (isEditing) {
      const fetchOrder = async () => {
        setLoading(true);
        try {
          const order = await getOrderById(id);
          form.setFieldsValue({
            customer_id: order.customer_id,
            customer_po_number: order.customer_po_number || "",
            priority: order.priority || "normal",
            expected_delivery: order.expected_delivery
              ? moment(order.expected_delivery)
              : null,
            discount_percent: order.discount_percent || 0,
            tax_rate: order.tax_rate || 5,
            shipping_address: order.shipping_address,
            notes: order.notes,
            internal_notes: order.internal_notes,
            pricing_type: order.pricing_type || "individual",
            combined_rate: order.combined_rate || 0,
            inners_per_pack: order.inners_per_pack || 0,
            outers_per_pack: order.outers_per_pack || 0,
          });
          setPricingType(order.pricing_type || "individual");
          setItems(order.items || []);
          // Fetch presigned URL for PO document
          if (order.po_document_key) {
            try {
              const { data: poData } = await axios.get(`po-document/order/${order.id}/po-document`);
              setPoDocUrl(poData.url);
            } catch { setPoDocUrl(null); }
          } else {
            setPoDocUrl(null);
          }
          // Load marking info
          if (order.po_marking_key) {
            setMarkingKey(order.po_marking_key);
            setMarkingFilename(order.po_marking_key.split("/").pop());
          }
        } catch (error) {
          toast.error("Failed to load order");
          console.log(error.message);
        } finally {
          setLoading(false);
        }
      };
      fetchOrder();
    }
  }, [id, isEditing, form]);

  // Clone: pre-populate from source order
  useEffect(() => {
    if (!isEditing && cloneFromId) {
      const fetchSource = async () => {
        setLoading(true);
        try {
          const order = await getOrderById(cloneFromId);
          form.setFieldsValue({
            customer_id: order.customer_id,
            customer_po_number: order.customer_po_number || "",
            priority: order.priority || "normal",
            expected_delivery: order.expected_delivery
              ? moment(order.expected_delivery)
              : null,
            discount_percent: order.discount_percent || 0,
            tax_rate: order.tax_rate || 5,
            shipping_address: order.shipping_address,
            notes: order.notes,
            internal_notes: order.internal_notes,
            pricing_type: order.pricing_type || "individual",
            combined_rate: order.combined_rate || 0,
            inners_per_pack: order.inners_per_pack || 0,
            outers_per_pack: order.outers_per_pack || 0,
          });
          setPricingType(order.pricing_type || "individual");
          // Strip IDs from items so they are treated as new
          setItems((order.items || []).map(({ id, order_id, created_at, updated_at, jobs, ...rest }) => rest));
        } catch (e) {
          toast.error("Failed to load source order for cloning");
        } finally {
          setLoading(false);
        }
      };
      fetchSource();
    }
  }, [cloneFromId, isEditing, form]);

  // Recalculate totals when items or form values change
  const recalculateTotals = useCallback(() => {
    const discount_percent = form.getFieldValue("discount_percent") || 0;
    const tax_rate = form.getFieldValue("tax_rate") || 5;
    const currentPricingType = form.getFieldValue("pricing_type") || pricingType;

    let subtotal = 0;

    if (currentPricingType === "inner_combined") {
      const combined_rate = Number(form.getFieldValue("combined_rate")) || 0;
      const inners = Number(form.getFieldValue("inners_per_pack")) || 0;
      subtotal = combined_rate * inners;
    } else if (currentPricingType === "outer_combined") {
      const combined_rate = Number(form.getFieldValue("combined_rate")) || 0;
      const outers = Number(form.getFieldValue("outers_per_pack")) || 0;
      subtotal = combined_rate * outers;
    } else {
      subtotal = items.reduce(
        (sum, item) => sum + (Number(item.total_amount) || 0),
        0
      );
    }

    const discount_amount = (subtotal * discount_percent) / 100;
    const taxable_amount = subtotal - discount_amount;
    const tax_amount = (taxable_amount * tax_rate) / 100;
    const total_amount = taxable_amount + tax_amount;

    setTotals({ subtotal, discount_amount, tax_amount, total_amount });
  }, [items, form, pricingType]);

  useEffect(() => {
    recalculateTotals();
  }, [items, recalculateTotals]);

  // Generate preview URL from local file or existing S3 URL
  useEffect(() => {
    if (poFile) {
      const url = URL.createObjectURL(poFile);
      setPreviewUrl(url);
      setFileType(poFile.type === 'application/pdf' ? 'pdf' : 'image');
      return () => URL.revokeObjectURL(url);
    } else if (poDocUrl) {
      setPreviewUrl(poDocUrl);
      // Extract file type from URL path (before query params)
      const urlPath = poDocUrl.split('?')[0].toLowerCase();
      setFileType(urlPath.endsWith('.pdf') ? 'pdf' : 'image');
    } else {
      setPreviewUrl(null);
      setFileType(null);
    }
  }, [poFile, poDocUrl]);

  // Handle add/edit item from modal
  const handleItemSave = (itemData) => {
    const incoming = Array.isArray(itemData) ? itemData : [itemData];
    if (editingItemIndex !== null) {
      const updatedItems = [...items];
      updatedItems[editingItemIndex] = incoming[0];
      setItems(updatedItems);
    } else {
      setItems((prev) => [...prev, ...incoming]);
    }
    setItemModalVisible(false);
    setEditingItemIndex(null);
  };

  const handleBulkItemsSave = (bulkItems) => {
    if (!Array.isArray(bulkItems) || bulkItems.length === 0) return;
    setItems((prev) => [...prev, ...bulkItems]);
    setBulkItemModalVisible(false);
  };

  // Handle delete item
  const handleDeleteItem = (index) => {
    const updatedItems = items.filter((_, i) => i !== index);
    setItems(updatedItems);
  };

  // Duplicate an existing item as a new one in the same order, then open it for editing so
  // small differences (dimensions, quantity, rate, ...) can be tweaked without re-entering
  // everything from scratch. Group fields are dropped — a clone is a distinct item, not another
  // parent/member of the same Item Group.
  const handleCloneItem = (index) => {
    const { item_group_id, qty_per_group, is_group_parent, ...rest } = items[index];
    const newIndex = items.length;
    setItems((prev) => [...prev, { ...rest }]);
    setEditingItemIndex(newIndex);
    setItemModalVisible(true);
  };

  // Handle form submit
  const handleSubmit = async (values) => {
    if (items.length === 0) {
      toast.error("Please add at least one item");
      return;
    }

    // PO-ingested items skip the "Add Item" modal's own required-subcategory validation (they're
    // pushed straight into `items`), so it has to be enforced here instead — same requirement,
    // just checked at a different point.
    const missingSubcategoryIndex = items.findIndex((item) => !item.subcategory);
    if (missingSubcategoryIndex !== -1) {
      toast.error(
        `Item #${missingSubcategoryIndex + 1} (${items[missingSubcategoryIndex].item_name || "unnamed"
        }) needs a subcategory — edit it before saving`
      );
      return;
    }

    setLoading(true);
    try {
      const orderData = {
        customer_id: values.customer_id,
        customer_po_number: values.customer_po_number || null,
        priority: values.priority || "normal",
        expected_delivery: values.expected_delivery
          ? values.expected_delivery.format("YYYY-MM-DD")
          : null,
        discount_percent: values.discount_percent || 0,
        tax_rate: values.tax_rate || 5,
        shipping_address: values.shipping_address,
        notes: values.notes,
        internal_notes: values.internal_notes,
        // Pricing type fields
        pricing_type: pricingType,
        combined_rate: ["inner_combined", "outer_combined"].includes(pricingType) ? Number(values.combined_rate) || 0 : null,
        inners_per_pack: ["inner_combined", "outer_combined"].includes(pricingType) ? Number(values.inners_per_pack) || 0 : null,
        outers_per_pack: ["inner_combined", "outer_combined"].includes(pricingType) ? Number(values.outers_per_pack) || 0 : null,
        items: items.map((item) => ({
          // Include id for updates (to identify existing items)
          ...(item.id && { id: item.id }),
          item_name: item.item_name,
          item_description: item.item_description,
          subcategory: item.subcategory,
          number_of_pieces: item.number_of_pieces || 1,
          length: Number(item.length),
          width: Number(item.width),
          height: Number(item.height),
          unit_type: item.unit_type,
          casting_type: item.casting_type,
          ply: Number(item.ply),
          layers:
            typeof item.layers === "string"
              ? item.layers
              : JSON.stringify(item.layers),
          is_printing: item.is_printing || false,
          printing_config:
            typeof item.printing_config === "string"
              ? item.printing_config
              : JSON.stringify(item.printing_config),
          is_coating: item.is_coating || false,
          coating_config:
            typeof item.coating_config === "string"
              ? item.coating_config
              : JSON.stringify(item.coating_config),
          joining_method: item.joining_method || null,
          roll_size: item.roll_size || 0,
          cutting_size: item.cutting_size || 0,
          net_gsm: item.net_gsm || 0,
          top_gsm: item.top_gsm || 0,
          box_weight: item.box_weight || 0,
          quantity: Number(item.quantity),
          rate_per_unit: Number(item.rate_per_unit || 0),
          total_amount: Number(item.total_amount || 0),
          // Combined packing fields
          packing_role: item.packing_role || null,
          qty_per_pack: item.qty_per_pack || null,
          item_group_id: item.item_group_id || null,
          qty_per_group: item.qty_per_group || null,
          is_group_parent: item.is_group_parent || null,
          // Calculation fields (may be empty for direct-rate orders)
          calculated_rate: Number(item.calculated_rate || 0),
          top_conversion_rate: Number(item.top_conversion_rate || 0),
          box_conversion_rate: Number(item.box_conversion_rate || 0),
          rs: item.rs || null,
          cs: item.cs || null,
          sheets_per_box: item.sheets_per_box || null,
          boxes_per_sheet: item.boxes_per_sheet || null,
          box_type: item.box_type || null,
          rate_type: item.rate_type || null,
          packing_quantity_rs: item.packing_quantity_rs || null,
          item_remarks: item.item_remarks || null,
          material_cost_top: item.material_cost_top || 0,
          material_cost_rest: item.material_cost_rest || 0,
          print_cost: item.print_cost || 0,
          coating_cost_total: item.coating_cost_total || 0,
          coating_cost_lamination: item.coating_cost_lamination || 0,
          coating_cost_leafing: item.coating_cost_leafing || 0,
          coating_cost_spot_uv: item.coating_cost_spot_uv || 0,
          fixed_cost_total: item.fixed_cost_total || 0,
          fixed_cost_per_box: item.fixed_cost_per_box || 0,
          markup_percentage: item.markup_percentage || 0,
          discount_percentage: item.discount_percentage || 0,
          selected_machines:
            typeof item.selected_machines === "string"
              ? item.selected_machines
              : JSON.stringify(item.selected_machines),
          outsource_jobs:
            typeof item.outsource_jobs === "string"
              ? item.outsource_jobs
              : JSON.stringify(item.outsource_jobs),
        })),
      };

      if (isEditing) {
        await updateOrder(id, orderData);
        if (poFile) await uploadPoDocument(id);
        if (markingFile) await uploadPoMarking(id);
        toast.success("Order updated successfully");
        navigate(`/order/${id}`);
      } else {
        const result = await createOrder(orderData);
        if (poFile && result.id) await uploadPoDocument(result.id);
        if (markingFile && result.id) await uploadPoMarking(result.id);
        if (pendingIngestionId && result.id) {
          try {
            await linkPoIngestion(pendingIngestionId, { order_id: result.id });
          } catch (e) {
            toast.warning(
              "Order created but linking the source PO failed: " + (e.response?.data?.message || e.message)
            );
          }
        }
        // Clone jobs from source order if requested
        if (cloneJobs && cloneFromId && result.id) {
          try {
            await axios.post(`order/${result.id}/clone-jobs`, { source_order_id: cloneFromId });
          } catch (e) {
            toast.warning("Order created but job cloning failed: " + (e.response?.data?.message || e.message));
          }
        }
        toast.success(
          `Order ${result.order_number || ""} created successfully`
        );
        navigate(`/order/${result.id}`);
      }
    } catch (error) {
      console.log(error.message);
      toast.error(
        isEditing ? "Failed to update order" : "Failed to create order"
      );
    } finally {
      setLoading(false);
    }
  };

  const uploadPoDocument = async (orderId) => {
    try {
      setUploadingPo(true);
      const formData = new FormData();
      formData.append("po_document", poFile);
      const { data } = await axios.post(`po-document/order/${orderId}/po-document`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // Fetch presigned URL for display
      try {
        const { data: poData } = await axios.get(`po-document/order/${orderId}/po-document`);
        setPoDocUrl(poData.url);
      } catch { setPoDocUrl(data.po_document_url); }
      setPoFile(null);
    } catch (error) {
      toast.error("Failed to upload PO document");
      console.error(error);
    } finally {
      setUploadingPo(false);
    }
  };

  const handlePoRemove = async () => {
    if (id && poDocUrl) {
      try {
        await axios.delete(`po-document/order/${id}/po-document`);
        toast.success("PO document removed");
      } catch (error) {
        toast.error("Failed to remove PO document");
      }
    }
    setPoDocUrl(null);
    setPoFile(null);
  };

  const uploadPoMarking = async (orderId) => {
    try {
      const formData = new FormData();
      formData.append("po_marking", markingFile);
      const { data } = await axios.post(`po-document/order/${orderId}/po-marking`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setMarkingKey(data.po_marking_key);
      setMarkingFilename(data.po_marking_key.split("/").pop());
      setMarkingFile(null);
    } catch (error) {
      toast.error("Failed to upload PO marking");
      console.error(error);
    }
  };

  const handleMarkingRemove = async () => {
    if (id && markingKey) {
      try {
        await axios.delete(`po-document/order/${id}/po-marking`);
        toast.success("PO marking removed");
      } catch (error) {
        toast.error("Failed to remove PO marking");
      }
    }
    setMarkingKey(null);
    setMarkingFilename(null);
    setMarkingFile(null);
  };

  // PO ingestion: after user confirms the review modal, pre-fill the form
  const handleIngestConfirm = ({ header, items: ingestItems, ingestionId }) => {
    // The Customer <Select>'s onChange (below) auto-fills shipping_address from the customer's
    // own address on file — but it only fires on a real user interaction, never when customer_id
    // is set programmatically via setFieldsValue like here. Replicate it: trust the PO's own
    // shipping address when the extraction actually found a distinct one, otherwise fall back to
    // the resolved customer's address exactly like the manual flow would, instead of leaving the
    // field blank.
    const resolvedCustomer = header.customer_id
      ? customers?.find((c) => c.id === header.customer_id)
      : null;
    const customerDefaultAddress = resolvedCustomer
      ? [resolvedCustomer.address, resolvedCustomer.pincode].filter(Boolean).join(", ")
      : "";

    form.setFieldsValue({
      ...(header.customer_id && { customer_id: header.customer_id }),
      customer_po_number: header.customer_po_number || "",
      ...(header.expected_delivery && { expected_delivery: moment(header.expected_delivery) }),
      shipping_address: header.shipping_address || customerDefaultAddress,
      notes: header.notes || "",
    });
    setItems((prev) => [...prev, ...ingestItems]);
    setPendingIngestionId(ingestionId);
    setIngestReviewOpen(false);
  };

  // Discarding a draft that was never used to pre-fill the form — clean it up server-side so
  // it doesn't linger as a "ready" record the SHA-256 cache could later hand back.
  const handleIngestDiscard = async () => {
    setIngestReviewOpen(false);
    if (currentIngest?.id) {
      try {
        await deletePoIngestion(currentIngest.id);
      } catch (e) {
        console.log("Failed to discard PO ingestion draft:", e.message);
      }
    }
  };

  // Check if any item has printing — marking becomes important
  const hasPrintingItems = items.some((item) => item.is_printing);
  const markingMissing = hasPrintingItems && !markingKey && !markingFile;

  // Auth check
  const isLogged = Boolean(localStorage.getItem("isLogged"));
  if (!isLogged) {
    return <Navigate to={"/auth/login"} replace={true} />;
  }
  const accessToken = localStorage.getItem("access-token");
  checkTokenExp(accessToken);

  // Item table columns
  const compareText = (left, right) =>
    String(left || "").localeCompare(String(right || ""), undefined, {
      numeric: true,
      sensitivity: "base",
    });

  const tableItems = items.map((item, index) => ({
    ...item,
    __sourceIndex: index,
    __rowKey: `${index}-${item.item_name || "item"}`,
  }));
  const normalizedItemSearch = itemSearch.trim().toLowerCase();
  const filteredTableItems = normalizedItemSearch
    ? tableItems.filter((item) => {
        const searchParts = [
          item.item_name,
          item.packing_role,
          item.qty_per_pack,
          item.subcategory,
          item.length,
          item.width,
          item.height,
          item.unit_type,
          item.ply,
          item.quantity,
          item.rate_per_unit,
          item.total_amount,
        ];

        return searchParts
          .filter((value) => value !== undefined && value !== null)
          .join(" ")
          .toLowerCase()
          .includes(normalizedItemSearch);
      })
    : tableItems;

  const itemColumns = [
    {
      title: "#",
      key: "index",
      width: 50,
      render: (_, __, index) => index + 1,
    },
    {
      title: "Item Name",
      dataIndex: "item_name",
      key: "item_name",
      width: 180,
      sorter: (a, b) => compareText(a.item_name, b.item_name),
    },
    ...(pricingType === "inner_combined" || pricingType === "outer_combined"
      ? [
          {
            title: "Role",
            dataIndex: "packing_role",
            key: "packing_role",
            width: 100,
            sorter: (a, b) => compareText(a.packing_role, b.packing_role),
            render: (role) => (
              <Tag color={role === "inner" ? "blue" : "green"}>
                {role === "inner" ? "Inner" : "Outer"}
              </Tag>
            ),
          },
          {
            title: "Qty/Pack",
            dataIndex: "qty_per_pack",
            key: "qty_per_pack",
            align: "right",
            width: 90,
            sorter: (a, b) =>
              (Number(a.qty_per_pack) || 0) - (Number(b.qty_per_pack) || 0),
          },
        ]
      : []),
    {
      title: "Box Type",
      dataIndex: "subcategory",
      key: "subcategory",
      sorter: (a, b) => compareText(a.subcategory, b.subcategory),
    },
    {
      title: "Dimensions",
      key: "dimensions",
      sorter: (a, b) =>
        (Number(a.length) || 0) - (Number(b.length) || 0) ||
        (Number(a.width) || 0) - (Number(b.width) || 0) ||
        (Number(a.height) || 0) - (Number(b.height) || 0),
      render: (_, record) =>
        `${record.length} × ${record.width} × ${record.height} ${record.unit_type || ""}`,
    },
    {
      title: "Ply",
      dataIndex: "ply",
      key: "ply",
      sorter: (a, b) => (Number(a.ply) || 0) - (Number(b.ply) || 0),
      render: (ply) => `${ply} Ply`,
      align: "center",
    },
    {
      title: pricingType === "inner_combined" || pricingType === "outer_combined" ? "Total Qty" : "Qty",
      dataIndex: "quantity",
      key: "quantity",
      sorter: (a, b) => (Number(a.quantity) || 0) - (Number(b.quantity) || 0),
      align: "right",
    },
    ...(pricingType === "individual"
      ? [
          {
            title: "Rate/Unit",
            dataIndex: "rate_per_unit",
            key: "rate_per_unit",
            sorter: (a, b) =>
              (Number(a.rate_per_unit) || 0) - (Number(b.rate_per_unit) || 0),
            render: (rate) => `₹${Number(rate || 0).toFixed(2)}`,
            align: "right",
          },
          {
            title: "Total",
            dataIndex: "total_amount",
            key: "total_amount",
            sorter: (a, b) =>
              (Number(a.total_amount) || 0) - (Number(b.total_amount) || 0),
            render: (amount) =>
              `₹${Number(amount || 0).toLocaleString("en-IN", {
                minimumFractionDigits: 2,
              })}`,
            align: "right",
          },
        ]
      : []),
    {
      title: "Actions",
      key: "actions",
      width: 130,
      fixed: "right",
      render: (_, record) => (
        <Space>
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => {
              setEditingItemIndex(record.__sourceIndex);
              setItemModalVisible(true);
            }}
          />
          <Button
            size="small"
            icon={<CopyOutlined />}
            title="Clone item"
            onClick={() => handleCloneItem(record.__sourceIndex)}
          />
          <Button
            size="small"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDeleteItem(record.__sourceIndex)}
          />
        </Space>
      ),
    },
  ];

  return (
    <>
      <PageTitle title="Back" subtitle={cloneFromId ? "Clone Order" : isEditing ? "Edit Order" : "New Order"} />
      <Title level={4} className="m-2 text-center" style={{ marginTop: 16 }}>
        {isEditing ? "Edit Order" : "New Order"}
      </Title>

      <Row className="mr-top" gutter={[16, 16]}>
        {/* PO Document Upload & Preview Panel */}
        <Col xs={24} lg={previewUrl ? 10 : 24}>
          <div style={previewUrl ? { position: 'sticky', top: 80 } : {}}>
            <Card bordered={false} className="rounded column-design">
              {previewUrl ? (
                <>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <Title level={5} style={{ margin: 0 }}>📄 PO Reference</Title>
                    <Space size="small">
                      <Button
                        size="small"
                        icon={<EyeOutlined />}
                        onClick={() => window.open(previewUrl, '_blank')}
                      >
                        Open
                      </Button>
                      <Upload
                        beforeUpload={(file) => { setPoFile(file); return false; }}
                        maxCount={1}
                        accept=".pdf,image/*"
                        showUploadList={false}
                      >
                        <Button size="small" icon={<UploadOutlined />}>Replace</Button>
                      </Upload>
                      <Button
                        size="small"
                        danger
                        onClick={handlePoRemove}
                      >
                        Remove
                      </Button>
                    </Space>
                  </div>
                  <div style={{ border: '1px solid #d9d9d9', borderRadius: 4, overflow: 'hidden', background: '#f5f5f5' }}>
                    {fileType === 'pdf' ? (
                      <iframe
                        src={previewUrl}
                        style={{ width: '100%', height: 'calc(100vh - 220px)', border: 'none' }}
                        title="PO Document Preview"
                      />
                    ) : (
                      <img
                        src={previewUrl}
                        alt="PO Document"
                        style={{ width: '100%', maxHeight: 'calc(100vh - 220px)', objectFit: 'contain', display: 'block' }}
                      />
                    )}
                  </div>
                  {poFile && (
                    <div style={{ marginTop: 8, color: '#8c8c8c', fontSize: 12 }}>
                      📎 {poFile.name} — will be uploaded when order is saved
                    </div>
                  )}
                </>
              ) : (
                <Upload.Dragger
                  beforeUpload={(file) => { setPoFile(file); return false; }}
                  maxCount={1}
                  accept=".pdf,image/*"
                  showUploadList={false}
                  style={{ padding: '20px 0' }}
                >
                  <p className="ant-upload-drag-icon">
                    <InboxOutlined style={{ fontSize: 48, color: '#1890ff' }} />
                  </p>
                  <p className="ant-upload-text" style={{ fontSize: 16, fontWeight: 500 }}>
                    Step 1: Upload Customer PO Document
                  </p>
                  <p className="ant-upload-hint">
                    Drag & drop or click to upload. Supported: PDF, JPG, PNG (Max 10MB).
                    <br />
                    The PO will be shown here as a reference while you fill the order details.
                  </p>
                </Upload.Dragger>
              )}
            </Card>
          </div>
        </Col>

        {/* Order Form Panel */}
        <Col xs={24} lg={previewUrl ? 14 : 24} className="rounded column-design">
          {!isEditing && (
            <PoIngestDropzone
              onIngested={(draft, file) => {
                setCurrentIngest(draft);
                setIngestReviewOpen(true);
                // Same PDF used for extraction becomes the order's attached PO document too —
                // otherwise it's used once for OCR and then never referenced again.
                if (file) setPoFile(file);
              }}
              customerIdHint={form.getFieldValue("customer_id")}
            />
          )}
          <Card bordered={false}>
            <Form
              form={form}
              layout="vertical"
              onFinish={handleSubmit}
              initialValues={{
                priority: "normal",
                discount_percent: 0,
                tax_rate: 5,
                pricing_type: "individual",
                combined_rate: 0,
                inners_per_pack: 0,
                outers_per_pack: 0,
              }}
            >
              {/* Customer & Basic Info */}
              <Card
                title="Basic Information"
                bordered={false}
                style={{ marginBottom: 16 }}
              >
                <Row gutter={16}>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="customer_id"
                      label="Customer"
                      rules={[
                        {
                          required: true,
                          message: "Please select a customer",
                        },
                      ]}
                    >
                      <Select
                        showSearch
                        placeholder="Select customer"
                        loading={!customers}
                        optionFilterProp="children"
                        filterOption={(input, option) =>
                          option.children
                            .toLowerCase()
                            .includes(input.toLowerCase())
                        }
                        filterSort={(optionA, optionB) =>
                          optionA?.children
                            .toLowerCase()
                            .localeCompare(optionB?.children.toLowerCase())
                        }
                        onChange={(customerId) => {
                          const selected = customers?.find(
                            (c) => c.id === customerId
                          );
                          if (selected) {
                            const addr = [
                              selected.address,
                              selected.pincode,
                            ]
                              .filter(Boolean)
                              .join(", ");
                            form.setFieldsValue({
                              shipping_address: addr,
                            });
                          }
                        }}
                      >
                        {customers &&
                          customers.map((cust) => (
                            <Option key={cust.id} value={cust.id}>
                              {cust.name}
                            </Option>
                          ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={6}>
                    <Form.Item
                      name="customer_po_number"
                      label="PO Number"
                    >
                      <Input placeholder="Customer PO #" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={6}>
                    <Form.Item
                      name="priority"
                      label="Priority"
                      rules={[{ required: true }]}
                    >
                      <Select>
                        <Option value="urgent">Urgent</Option>
                        <Option value="high">High</Option>
                        <Option value="normal">Normal</Option>
                        <Option value="low">Low</Option>
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={16}>
                  <Col xs={24} md={8}>
                    <Form.Item
                      name="expected_delivery"
                      label="Expected Delivery"
                    >
                      <DatePicker
                        style={{ width: "100%" }}
                        format="DD-MM-YYYY"
                        disabledDate={(current) =>
                          current && current < moment().startOf("day")
                        }
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={8}>
                    <Form.Item name="discount_percent" label="Discount (%)">
                      <InputNumber
                        min={0}
                        max={100}
                        style={{ width: "100%" }}
                        onChange={() => {
                          setTimeout(recalculateTotals, 0);
                        }}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={8}>
                    <Form.Item name="tax_rate" label="Tax Rate (%)">
                      <InputNumber
                        min={0}
                        max={28}
                        style={{ width: "100%" }}
                        onChange={() => {
                          setTimeout(recalculateTotals, 0);
                        }}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </Card>

              {/* Line Items */}
              <Card
                title="Order Items"
                bordered={false}
                style={{ marginBottom: 16 }}
                extra={
                  <Space wrap>
                    <Input.Search
                      allowClear
                      placeholder="Search order items"
                      value={itemSearch}
                      onChange={(e) => setItemSearch(e.target.value)}
                      style={{ width: 240 }}
                    />
                    <Button
                      icon={<InboxOutlined />}
                      onClick={() => {
                        setBulkItemModalVisible(true);
                      }}
                    >
                      Add Bulk Items
                    </Button>
                    <Button
                      type="primary"
                      icon={<PlusOutlined />}
                      onClick={() => {
                        setEditingItemIndex(null);
                        setItemModalVisible(true);
                      }}
                    >
                      Add Item
                    </Button>
                  </Space>
                }
              >
                <Table
                  columns={itemColumns}
                  dataSource={filteredTableItems}
                  rowKey="__rowKey"
                  pagination={false}
                  scroll={{ x: 900 }}
                  locale={{
                    emptyText:
                      pricingType === "inner_combined" || pricingType === "outer_combined"
                        ? "No items added. Click 'Add Item' to add inner/outer box specs for this pack."
                        : "No items added. Click 'Add Item' to add a box with its rate.",
                  }}
                />

                {/* Group Pricing Summary */}
                {items.some((i) => i.item_group_id) && (() => {
                  const groupMap = {};
                  items.forEach((item) => {
                    if (!item.item_group_id) return;
                    if (!groupMap[item.item_group_id]) groupMap[item.item_group_id] = [];
                    groupMap[item.item_group_id].push(item);
                  });
                  return (
                    <div style={{ marginTop: 16 }}>
                      {Object.entries(groupMap).map(([gid, gItems]) => {
                        const parent = gItems.find((i) => i.is_group_parent);
                        const groupTotal = gItems.reduce(
                          (sum, i) => sum + (i.rate_per_unit || 0) * (i.qty_per_group || 1),
                          0
                        );
                        const parentQtyPerGroup = parent?.qty_per_group || 1;
                        return (
                          <Card
                            key={gid}
                            size="small"
                            title={
                              <span style={{ color: "#1677ff" }}>
                                Group: {parent?.item_name || "Item Group"} set
                              </span>
                            }
                            style={{ marginBottom: 8, borderColor: "#91caff", background: "#f0f9ff" }}
                          >
                            <Row gutter={[8, 4]}>
                              {gItems.map((item, i) => (
                                <Col key={i} xs={24}>
                                  <Row justify="space-between">
                                    <Col>
                                      {item.is_group_parent && <Tag color="blue">parent</Tag>}
                                      {item.qty_per_group}× {item.item_name}
                                      <span style={{ color: "#888", fontSize: 12 }}>
                                        {" "}@ ₹{Number(item.rate_per_unit || 0).toFixed(2)}
                                      </span>
                                    </Col>
                                    <Col>
                                      ₹{(Number(item.rate_per_unit || 0) * (item.qty_per_group || 1)).toFixed(2)}
                                    </Col>
                                  </Row>
                                </Col>
                              ))}
                              <Col xs={24}><Divider style={{ margin: "4px 0" }} /></Col>
                              <Col xs={24}>
                                <Row justify="space-between">
                                  <Col><strong>Group total (1 set)</strong></Col>
                                  <Col><strong>₹{groupTotal.toFixed(2)}</strong></Col>
                                </Row>
                              </Col>
                              {parent && (
                                <Col xs={24}>
                                  <Row justify="space-between">
                                    <Col>
                                      <span style={{ color: "#1677ff" }}>
                                        {parent.item_name}-wise price
                                        {parentQtyPerGroup > 1 && ` (÷ ${parentQtyPerGroup})`}
                                      </span>
                                    </Col>
                                    <Col>
                                      <strong style={{ fontSize: 16, color: "#1677ff" }}>
                                        ₹{(groupTotal / parentQtyPerGroup).toFixed(2)}
                                      </strong>
                                    </Col>
                                  </Row>
                                </Col>
                              )}
                              <Col xs={24}>
                                <div style={{ fontSize: 12, color: "#888", marginTop: 4 }}>
                                  All member-wise: {gItems.map((i) =>
                                    `${i.item_name}: ₹${(i.qty_per_group ? groupTotal / i.qty_per_group : 0).toFixed(2)}`
                                  ).join(" | ")}
                                </div>
                              </Col>
                            </Row>
                          </Card>
                        );
                      })}
                    </div>
                  );
                })()}

                {/* Totals */}
                {(items.length > 0 || pricingType === "inner_combined" || pricingType === "outer_combined") && (
                  <Row justify="end" style={{ marginTop: 24 }}>
                    <Col xs={24} md={8}>
                      <div
                        style={{
                          background: "#fafafa",
                          padding: 16,
                          borderRadius: 8,
                        }}
                      >
                        <Row
                          justify="space-between"
                          style={{ marginBottom: 8 }}
                        >
                          <span>Subtotal:</span>
                          <strong>
                            ₹
                            {totals.subtotal.toLocaleString("en-IN", {
                              minimumFractionDigits: 2,
                            })}
                          </strong>
                        </Row>
                        <Row
                          justify="space-between"
                          style={{ marginBottom: 8 }}
                        >
                          <span>
                            Discount (
                            {form.getFieldValue("discount_percent") || 0}%):
                          </span>
                          <span style={{ color: "#f5222d" }}>
                            - ₹
                            {totals.discount_amount.toLocaleString("en-IN", {
                              minimumFractionDigits: 2,
                            })}
                          </span>
                        </Row>
                        <Row
                          justify="space-between"
                          style={{ marginBottom: 8 }}
                        >
                          <span>
                            Tax ({form.getFieldValue("tax_rate") || 5}%):
                          </span>
                          <span>
                            ₹
                            {totals.tax_amount.toLocaleString("en-IN", {
                              minimumFractionDigits: 2,
                            })}
                          </span>
                        </Row>
                        <Divider style={{ margin: "8px 0" }} />
                        <Row
                          justify="space-between"
                          style={{ fontSize: 16, fontWeight: "bold" }}
                        >
                          <span>Total:</span>
                          <span>
                            ₹
                            {totals.total_amount.toLocaleString("en-IN", {
                              minimumFractionDigits: 2,
                            })}
                          </span>
                        </Row>
                      </div>
                    </Col>
                  </Row>
                )}
              </Card>

              {/* PO Marking / Artwork Upload */}
              <Card
                title={
                  <Space>
                    <span>PO Marking / Artwork</span>
                    {markingMissing && <Tag color="red">⚠ Missing — Items have printing</Tag>}
                    {(markingKey || markingFile) && <Tag color="green">Uploaded</Tag>}
                  </Space>
                }
                bordered={false}
                style={{ marginBottom: 16 }}
                size="small"
              >
                {markingKey || markingFile ? (
                  <Space>
                    <FilePdfOutlined style={{ fontSize: 18 }} />
                    <span>{markingFile ? markingFile.name : markingFilename}</span>
                    {markingKey && (
                      <Button
                        size="small"
                        type="link"
                        onClick={async () => {
                          try {
                            const { data } = await axios.get(`po-document/order/${id}/po-marking`);
                            window.open(data.url, '_blank');
                          } catch { toast.error("Failed to open marking"); }
                        }}
                      >
                        View
                      </Button>
                    )}
                    <Button size="small" danger onClick={handleMarkingRemove}>Remove</Button>
                  </Space>
                ) : (
                  <Upload
                    beforeUpload={(file) => { setMarkingFile(file); return false; }}
                    maxCount={1}
                    accept=".pdf,.cdr"
                    showUploadList={false}
                  >
                    <Button icon={<UploadOutlined />}>Upload Marking (PDF / CDR)</Button>
                  </Upload>
                )}
                {markingFile && !markingKey && (
                  <div style={{ marginTop: 8, color: '#8c8c8c', fontSize: 12 }}>
                    📎 {markingFile.name} — will be uploaded when order is saved
                  </div>
                )}
              </Card>

              {/* Notes & Shipping */}
              <Card
                title="Shipping & Notes"
                bordered={false}
                style={{ marginBottom: 16 }}
              >
                <Row gutter={16}>
                  <Col xs={24} md={8}>
                    <Form.Item
                      name="shipping_address"
                      label="Shipping Address"
                    >
                      <TextArea
                        rows={4}
                        placeholder="Delivery / shipping address..."
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={8}>
                    <Form.Item
                      name="notes"
                      label="Notes (visible to customer)"
                    >
                      <TextArea
                        rows={4}
                        placeholder="Add notes for the customer..."
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={8}>
                    <Form.Item
                      name="internal_notes"
                      label="Internal Notes (not visible to customer)"
                    >
                      <TextArea
                        rows={4}
                        placeholder="Internal notes for your team..."
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </Card>

              {/* Actions */}
              <Row justify="end" gutter={16} align="middle">
                {!isEditing && cloneFromId && (
                  <Col>
                    <Checkbox
                      checked={cloneJobs}
                      onChange={(e) => setCloneJobs(e.target.checked)}
                    >
                      Clone Jobs & Instructions from source
                    </Checkbox>
                  </Col>
                )}
                <Col>
                  <Button onClick={() => navigate("/order")}>Cancel</Button>
                </Col>
                <Col>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={loading}
                    shape="round"
                  >
                    {isEditing ? "Update Order" : "Create Order"}
                  </Button>
                </Col>
              </Row>
            </Form>
          </Card>
        </Col>
      </Row>

      {/* Item Modal — Order-specific modal with direct rate input */}
      <OrderItemModal
        visible={itemModalVisible}
        onCancel={() => {
          setItemModalVisible(false);
          setEditingItemIndex(null);
        }}
        onSave={handleItemSave}
        initialData={
          editingItemIndex !== null ? items[editingItemIndex] : null
        }
        pricingType={pricingType}
      />
      <OrderBulkItemsModal
        visible={bulkItemModalVisible}
        onCancel={() => setBulkItemModalVisible(false)}
        onSave={handleBulkItemsSave}
        pricingType={pricingType}
      />
      <PoIngestReviewModal
        open={ingestReviewOpen}
        ingestion={currentIngest}
        onConfirm={handleIngestConfirm}
        onDiscard={handleIngestDiscard}
        // Close has no "reopen later" affordance anywhere on this page — an unconfirmed draft
        // left behind here is exactly as orphaned as one explicitly discarded, so it gets the
        // same server-side cleanup.
        onClose={handleIngestDiscard}
        afterClose={() => setCurrentIngest(null)}
      />
    </>
  );
};

export default OrderForm;
